import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { Icon } from "@fluentui/react/lib/Icon";
import { TooltipHost } from "@fluentui/react";

import AssociationListing from "../association-listing/AssociationListing";
import AttachDocument from "../attach-document/AttachDocument";
import { associationPartOneApiCall, associationPartTwoApiCall } from "../redux/apiCalls";
import { startFetchingAssociationsAttachFromIci } from "../redux/slice";
import {
  setSelectedAgreementData,
  transformAssociationPartOneData,
  transformAssociationPartTwoData,
  updateAssociationData,
} from "../utils";
import { TEXT } from "../../../shared/constants/texts";
import { ICON } from "../../../shared/constants/icons";
import { logError } from "../../../shared/services/errorLogging";

type AgreementListingProps = {
  agreements: any;
  getMoreAgreementData: any;
  hasMoreAgreements: any;
};

const AgreementListing: React.FC<AgreementListingProps> = (props) => {
  const { agreements, getMoreAgreementData, hasMoreAgreements } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.attachFromIciSlice);
  const { isFetchingAgreementData, selectedAgreement, tooltipAttributes } = state;
  const [showAssociationId, setShowAssociationId] = React.useState(null);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (selectedAgreement) {
      getAssociations();
    } else {
      setShowAssociationId(null);
    }
  }, [selectedAgreement]);

  const getAssociations = async () => {
    dispatch(startFetchingAssociationsAttachFromIci());
    const { sysId, entityName } = selectedAgreement;
    const requestDataPartOne = { sysId: sysId, entityName: entityName };
    const associationPartOneResponse = await associationPartOneApiCall({
      ...apiCallParams,
      requestData: requestDataPartOne,
      retryFunctionCall: () => getAssociations(),
    });
    const { response: responsePartOne, error: errorPartOne } = associationPartOneResponse;
    if (responsePartOne) {
      const associationIds = transformAssociationPartOneData(responsePartOne);
      const requestDataPartTwo = { ids: associationIds };
      const associationPartTwoResponse = await associationPartTwoApiCall({
        ...apiCallParams,
        requestData: requestDataPartTwo,
        retryFunctionCall: () => getAssociations(),
      });
      const { response: responsePartTwo, error: errorPartTwo } = associationPartTwoResponse;
      if (responsePartTwo) {
        const associations = transformAssociationPartTwoData({ responsePartOne, responsePartTwo });
        updateAssociationData(associations);
      }
      if (errorPartTwo) {
        updateAssociationData([]);
        logError({
          dispatch: dispatch,
          err: errorPartTwo,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|attach-from-ici|agreement-listing|AgreementListing - getAssociations",
            callerApi: "api/Instance/FindProjections",
          },
        });
      }
    }
    if (errorPartOne) {
      updateAssociationData([]);
      logError({
        dispatch: dispatch,
        err: errorPartOne,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|attach-from-ici|agreement-listing|AgreementListing - getAssociations",
          callerApi:
            "Agreement/GetAssociationsWithEvaluatedDynamicAssociation?agreementId=&contractType=&evaluateDynamicAssociations=true&skipAmendment=true&skipPeer=true&skipMaster=true",
        },
      });
    }
  };

  const onIconToggleClicked = (data) => {
    if (showAssociationId === data.sysId) {
      setSelectedAgreementData(null);
    } else {
      setShowAssociationId(data.sysId);
      setSelectedAgreementData(data);
    }
  };

  const onEnterPressAgreement = (e, agreement) => {
    if (e.key === TEXT.ENTER || e.keyCode === TEXT.ENTER_KEY_CODE) {
      onIconToggleClicked(agreement);
    }
  };

  return (
    <div id="id-agreement-listing-attach-from-ici" className="agreement-listing-container-attach-from-ici">
      {isFetchingAgreementData ? (
        <LoadingAgreements />
      ) : agreements && agreements.length > 0 ? (
        <InfiniteScroll
          dataLength={agreements.length}
          next={getMoreAgreementData}
          hasMore={hasMoreAgreements}
          loader={
            <div className="agreement-loader-container-is-attach-from-ici">
              <Spinner label={TEXT.LOADING_AGREEMENTS} labelPosition="right" />
            </div>
          }
          endMessage={
            <div className="agreement-end-message-container-is-attach-from-ici">
              <p>{TEXT.END_OF_THE_AGREEMENT_LIST}</p>
            </div>
          }
          scrollableTarget="id-agreement-listing-attach-from-ici"
        >
          {agreements.map((agreement) => {
            let classNameForSingleAgreement = "single-agreement-container-attach-from-ici";
            if (selectedAgreement && selectedAgreement.sysId === agreement.sysId) {
              classNameForSingleAgreement = "single-agreement-container-selected-attach-from-ici";
            }
            const iconName = showAssociationId === agreement.sysId ? ICON.UP : ICON.DOWN;
            return (
              <React.Fragment key={agreement.sysId}>
                <div className={classNameForSingleAgreement}>
                  <React.Fragment>
                    <div
                      className="icon-toggle"
                      onClick={() => onIconToggleClicked(agreement)}
                      tabIndex={1}
                      onKeyDown={(e) => onEnterPressAgreement(e, agreement)}
                      title={iconName}
                    >
                      <Icon iconName={iconName} />
                      <span className="sr-only">{iconName}</span>
                    </div>
                    <div className="agreement-label">
                      <TooltipHost
                        tooltipProps={{
                          onRenderContent: () => (
                            <React.Fragment>
                              {tooltipAttributes && tooltipAttributes.length > 0 ? (
                                <div className="agreement-tooltip-container-attach-from-ici">
                                  {tooltipAttributes.map((attr, index) => {
                                    for (let key in agreement) {
                                      if (attr.Key === key.toLowerCase()) {
                                        return (
                                          <p key={index}>
                                            <span>{attr.Value}</span>: {agreement[key]}
                                          </p>
                                        );
                                      }
                                    }
                                  })}
                                </div>
                              ) : (
                                <div className="agreement-tooltip-container-attach-from-ici">
                                  <p>
                                    <span>{TEXT.NAME}</span>: {agreement.name}
                                  </p>
                                  <p>
                                    <span>{TEXT.CREATED_BY}</span>: {agreement.createdBy}
                                  </p>
                                  <p>
                                    <span>{TEXT.AGREEMENT_CODE}</span>: {agreement.iCMAgreementCode}
                                  </p>
                                  <p>
                                    <span>{TEXT.STATUS}</span>: {agreement.status}
                                  </p>
                                </div>
                              )}
                            </React.Fragment>
                          ),
                        }}
                        id="agreement-tooltip-attach-from-ici"
                      >
                        <p>{agreement.name ? agreement.name : agreement.iCMAgreementCode}</p>
                      </TooltipHost>
                    </div>
                  </React.Fragment>
                </div>
                {showAssociationId === agreement.sysId ? (
                  <React.Fragment>
                    <AttachDocument data={agreement} fromAgreement={true} />
                    <AssociationListing />
                  </React.Fragment>
                ) : null}
              </React.Fragment>
            );
          })}
        </InfiniteScroll>
      ) : (
        <EmptyAgreements />
      )}
    </div>
  );
};

const LoadingAgreements = () => {
  return (
    <div className="agreement-listing-loading-container-attach-from-ici">
      <Spinner size={3} label={TEXT.LOADING_AGREEMENTS} />
    </div>
  );
};

const EmptyAgreements = () => {
  return (
    <div className="agreement-listing-empty-container-attach-from-ici">
      <div className="icon">
        <Icon iconName="TextDocument" />
        <span className="sr-only">{TEXT.TEXTDOCUMENT}</span>
      </div>
      <p>{TEXT.NO_AGREEMENTS_FOUND}</p>
    </div>
  );
};

export default AgreementListing;
